import { Button } from "antd";
import React from "react";
import { FormattedMessage } from "react-intl";
import {
  AcceptQuickContractRequest,
  ContractAcceptRequest,
  ContractRejectRequest,
  UpdateContractRequest as UpdateSecondaryStatusContractRequest,
  UpdateContractRequest,
} from "../../api/contractsApi/acceptRejectApi";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import STRINGS from "../../utils/UserTypes";
import { checkAccountConfirmation } from "../../utils/Helper";
import { GetContractDetails } from "../../api/contractsApi/contractDetails";
import { isStripe } from "../../utils/paymentProvider";
import { ROUTES } from "../../constants/routes";
import { useStripeContractCharge } from "../../hooks/useStripeContractCharge";
import PaymentElementModal from "../Stripe/PaymentElementModal";
import {
  isQuickContractPayeeSender,
  isQuickContractReceiver,
  isGigsterAcceptOnUnfundedContract,
} from "./stripeContractHelpers";

export const ContractDetailMainButton = ({
  userData,
  contractDetail,
  userTypeData,
  setConfirmChargesModal,
  setPaymentType,
  setAccountInfo,
  setLoading,
  setSignatureModal,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    chargeViaStripe,
    preparing: chargePreparing,
    modalProps: stripeChargeModalProps,
  } = useStripeContractCharge();

  const isContractUpdated =
    contractDetail?.secondary_status ===
      "contractor_changed_requirements_gigster" &&
    userData?.user_type === "tradesman";

  const isQuickPayContract =
    (contractDetail?.status === "direct_pay_to_tradesman" &&
      userData?.user_type === "tradesman") ||
    (contractDetail?.status === "direct_pay_to_contractor" &&
      userData?.user_type === "contractor") ||
    (contractDetail?.status === "direct_pay_to_homeowner" &&
      userData?.user_type === "homeowner");

  // A payee-sender (gigster→contractor OR contractor→homeowner) is not the payer
  // and never pays here — the payer pays on their accept.
  const isPayeeSender = isQuickContractPayeeSender(
    contractDetail,
    userData?.user_type
  );

  // "Pay now" retry: a quick contract the payer-sender CREATED but hasn't paid
  // (e.g. the payee wasn't payout-ready at submit, so the charge was refused).
  // This gate intentionally reads `is_paid` (a CAPTURED charge), not `is_funded`:
  // quick contracts charge outright at submit, so there is no hold stage and a
  // settled charge is exactly what "already paid" means here. Standard contracts
  // use `is_funded` because their money sits held until the gigster accepts.
  // the backend flips primary_status to WAITING at sheet-create, so status can't
  // distinguish paid from unpaid. Mirrors mobile `isUnpaidQuickContractSender`.
  const isUnpaidQuickContractSender =
    isStripe() &&
    contractDetail?.status?.startsWith("direct_pay_") &&
    !isQuickPayContract &&
    !isPayeeSender &&
    userData?.user_type !== "tradesman" &&
    contractDetail?.is_paid === false;

  // G1 PAY-NOW retry: re-run the PaymentSheet for the created-but-unpaid contract.
  // On a confirmed charge, re-fetch the contract so the backend-derived `is_funded`
  // flips true and this button drops — the Pay-Now flow stays on this screen (unlike
  // accept/re-init, which navigate away), so it must refresh in place itself.
  const handlePayNow = async () => {
    const paid = await chargeViaStripe(contractDetail?.id);
    if (paid) {
      await GetContractDetails(contractDetail?.id, dispatch);
    }
  };

  // G3: a quick-contract RECEIVER (payee-accepter) who isn't payout-ready must set up
  // Stripe payouts before accepting a contract that can't otherwise pay out. Redirects
  // to onboarding and reports that it handled the tap. Mirrors mobile's accept guard.
  const redirectIfPayeeNotOnboarded = () => {
    if (
      isStripe() &&
      isQuickContractReceiver(userData?.user_type, getContractedUserType()) &&
      !userTypeData?.receivingAccountVerify
    ) {
      navigate(ROUTES.STRIPE_ONBOARDING);
      return true;
    }
    return false;
  };

  const handleAccept = async () => {
    if (redirectIfPayeeNotOnboarded()) {
      return;
    }
    setLoading(true);
    if (isStripe() || userTypeData?.sendingAccountVerify) {
      if (
        userData?.user_type === "contractor" ||
        userData?.user_type === "homeowner" ||
        (contractDetail?.contractor &&
          contractDetail?.homeowner &&
          userData?.user_type !== "tradesman")
      ) {
        if (userData?.user_type === "contractor") {
          setConfirmChargesModal(true);
        } else {
          const body = {
            contract_Id: contractDetail?.id,
            contractDetail,
          };
          await ContractAcceptRequest(body, dispatch);
        }
      } else {
        if (
          ["running", "receive"].includes(contractDetail?.status) &&
          contractDetail?.secondary_status ===
            "contractor_changed_requirements_gigster" &&
          userData?.user_type === "tradesman"
        ) {
          const body = {
            contract_Id: contractDetail?.parentContractId,
            secondary_status: "gigster_accepted_changes",
            isSubContract: true,
            subContractId: contractDetail?.id,
            contractDetail,
          };
          await UpdateContractRequest(body, navigate);
        } else {
          const body = {
            contract_Id: contractDetail?.id,
            contractDetail,
          };
          await ContractAcceptRequest(body, dispatch, navigate);
        }
      }
    } else {
      setPaymentType("receive");
      setAccountInfo(true);
    }
    setLoading(false);
  };
  const handleReject = async () => {
    setLoading(true);
    if (isContractUpdated) {
      const body = {
        contract_Id: contractDetail?.parentContractId,
        secondary_status: "gigster_rejected_changes",
        isSubContract: true,
        subContractId: contractDetail?.id,
      };
      await UpdateSecondaryStatusContractRequest(body, navigate);
    } else {
      const body = {
        contract_Id: contractDetail?.id,
        status: "reject",
      };
      await ContractRejectRequest(body, dispatch, navigate);
      setLoading(false);
    }
  };

  const getContractedUserType = () => {
    if (userData?.user_type === STRINGS.HOMEOWNER_TYPE) {
      return contractDetail?.contractorId
        ? STRINGS.CONTRACTOR_TYPE
        : STRINGS.TRADESMAN_TYPE;
    } else if (userData?.user_type === STRINGS.CONTRACTOR_TYPE) {
      return contractDetail?.homeownerId
        ? STRINGS.HOMEOWNER_TYPE
        : STRINGS.TRADESMAN_TYPE;
    } else {
      return contractDetail?.contractorId
        ? STRINGS.CONTRACTOR_TYPE
        : STRINGS.HOMEOWNER_TYPE;
    }
  };

  const acceptQuickContract = async () => {
    if (redirectIfPayeeNotOnboarded()) {
      return;
    }
    const contractedUserType = getContractedUserType();
    const result = checkAccountConfirmation(
      userData?.user_type,
      contractedUserType,
      contractedUserType === STRINGS.HOMEOWNER_TYPE &&
        userData?.user_type === STRINGS.CONTRACTOR_TYPE
        ? userTypeData?.receivingAccountVerify
        : userTypeData?.sendingAccountVerify || isStripe(),
      setAccountInfo
    );
    setPaymentType(result === "sender" ? "send" : result);
    if (!result) {
      if (userData?.user_type === STRINGS.HOMEOWNER_TYPE) {
        // G6: under Stripe the homeowner charges directly (no signature) — mobile
        // has no signature step. Legacy AeroPay keeps the signed record.
        if (isStripe()) {
          setConfirmChargesModal(true);
        } else {
          setSignatureModal(true);
        }
      } else if (
        userData?.user_type === STRINGS.CONTRACTOR_TYPE &&
        contractedUserType === STRINGS.TRADESMAN_TYPE
      ) {
        setConfirmChargesModal(true);
      } else {
        await AcceptQuickContractRequest(
          {
            contractId: contractDetail?.id,
            status: "accept",
          },
          navigate
        );
      }
    }
  };

  if (isUnpaidQuickContractSender) {
    return (
      <div className="mb-5">
        <Button
          className="w-100 fw-bold"
          style={{
            backgroundColor: "#f77e0b",
            height: "56px",
            fontSize: "18px",
            color: "white",
          }}
          loading={chargePreparing}
          onClick={handlePayNow}
        >
          <FormattedMessage id="PAY_NOW" />
        </Button>
        <PaymentElementModal
          {...stripeChargeModalProps}
          title={<FormattedMessage id="CONTRACT_AMOUNT_DETAIL" />}
        />
      </div>
    );
  }

  if (isQuickPayContract) {
    return (
      <div className="date-div mb-5">
        <Button
          className="w-100 fw-bold mx-1 acceptContractBtn"
          onClick={acceptQuickContract}
        >
          {<FormattedMessage id="ACCEPT" />}
        </Button>
      </div>
    );
  }

  if (
    ((contractDetail?.status === "send" &&
      contractDetail?.contractor &&
      userData?.user_type === "tradesman") ||
      (contractDetail?.status === "pending_approval" &&
        userData?.user_type === "homeowner") ||
      (contractDetail?.status === "pending_approval" &&
        userData?.user_type === "contractor") ||
      (contractDetail?.status === "assigned" &&
        userData?.user_type === "tradesman") ||
      (["running", "receive"].includes(contractDetail?.status) &&
        contractDetail?.secondary_status ===
          "contractor_changed_requirements_gigster" &&
        userData?.user_type === "tradesman")) &&
    // Suppress the Reject/Accept pair while a payment is in-flight/blocked — the
    // payer must fund the contract first (mobile parity, contract_detail_main_button
    // :228). Does not affect quick-pay contracts, which render above.
    !["waiting", "blocked"].includes(contractDetail?.primary_status) &&
    // A gigster can't accept a standard contract the payer never funded: when the
    // payer skips the payment sheet, primary_status is never set to WAITING, so the
    // guard above passes — but is_funded is still false. Backend enforces this too.
    !isGigsterAcceptOnUnfundedContract(contractDetail, userData?.user_type)
  ) {
    return (
      <div className="date-div mb-5">
        <Button
          className="w-100 fw-bold mx-1"
          style={{
            color: "#F77e0b",
            border: "2px solid #F77e0b",
            height: "56px",
            width: "100px",
          }}
          onClick={handleReject}
        >
          {<FormattedMessage id="REJECT" />}
        </Button>
        <Button
          className="w-100 fw-bold mx-1 acceptContractBtn"
          onClick={handleAccept}
        >
          {<FormattedMessage id="ACCEPT" />}
        </Button>
      </div>
    );
  } else {
    return <></>;
  }
};

export const CancelContractButton = ({
  visible = false,
  contractDetail,
  setCancelModal,
}) => {
  return visible && !contractDetail?.secondary_status ? (
    <div className="mt-5">
      <Button
        onClick={() => setCancelModal(true)}
        className="w-100 fw-bold cancelContractButton"
      >
        <FormattedMessage id="CANCEL" />
      </Button>
    </div>
  ) : null;
};
